import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { api, ApiError } from './api';
import type { Me, UnlockedAchievement, Wallet } from './types';
import type { BadgeAward } from '../components/badges/BadgeFanfare';

/* -------------------------------------------------------------------------
   Toasts
   ------------------------------------------------------------------------- */

export type ToastKind = 'success' | 'error' | 'info' | 'reward';

export interface Toast {
  id: number;
  kind: ToastKind;
  icon: string;
  message: string;
}

interface ToastApi {
  toasts: Toast[];
  push: (kind: ToastKind, message: string, icon?: string) => void;
  dismiss: (id: number) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

const DEFAULT_ICON: Record<ToastKind, string> = {
  success: '✓',
  error: '!',
  info: 'i',
  reward: '★',
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(1);

  const dismiss = useCallback((id: number) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const push = useCallback(
    (kind: ToastKind, message: string, icon?: string) => {
      const id = nextId.current++;
      setToasts((current) => [...current.slice(-3), { id, kind, message, icon: icon ?? DEFAULT_ICON[kind] }]);
      window.setTimeout(() => dismiss(id), kind === 'error' ? 6000 : 4200);
    },
    [dismiss],
  );

  const value = useMemo(() => ({ toasts, push, dismiss }), [toasts, push, dismiss]);
  return <ToastContext.Provider value={value}>{children}</ToastContext.Provider>;
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) throw new Error('useToast must be used inside ToastProvider');
  return context;
}

/* -------------------------------------------------------------------------
   Session
   ------------------------------------------------------------------------- */

interface SessionApi {
  me: Me | null;
  loading: boolean;
  refresh: () => Promise<void>;
  login: (username: string, password: string) => Promise<void>;
  register: (username: string, password: string, trainerName?: string) => Promise<void>;
  /** Play Now. Creates a guest trainer and signs them in, with no form. */
  playAsGuest: () => Promise<void>;
  /** Turns the current guest into a permanent account, in place. */
  claim: (input: { username: string; email: string; password: string }) => Promise<void>;
  logout: () => Promise<void>;
  /** Applies a wallet returned by an action, with a bump animation. */
  setWallet: (wallet: Wallet | null | undefined) => void;
  patchMe: (patch: Partial<Me>) => void;
  walletBumped: boolean;
  /** Shows the achievement celebration queue. */
  celebrate: (unlocked: UnlockedAchievement[]) => void;
  pendingAchievements: UnlockedAchievement[];
  clearAchievement: () => void;
  /**
   * Shows the badge tier-up celebration queue.
   *
   * Separate from `celebrate` because the two are different events with
   * different weight: a milestone is a small "nice", a Master tier is the
   * biggest thing that happens on screen. One shared queue would have to pick
   * one treatment for both.
   */
  celebrateBadges: (awards: BadgeAward[]) => void;
  pendingBadges: BadgeAward[];
  clearBadges: () => void;
}

const SessionContext = createContext<SessionApi | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);
  const [walletBumped, setWalletBumped] = useState(false);
  const [pendingAchievements, setPendingAchievements] = useState<UnlockedAchievement[]>([]);
  const [pendingBadges, setPendingBadges] = useState<BadgeAward[]>([]);
  const bumpTimer = useRef<number>();

  const refresh = useCallback(async () => {
    try {
      setMe(await api.get<Me>('/me'));
    } catch (error) {
      // 401 simply means "not signed in" — not an error worth surfacing.
      if (error instanceof ApiError && error.status === 401) setMe(null);
      else if (!(error instanceof ApiError)) setMe(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const setWallet = useCallback((wallet: Wallet | null | undefined) => {
    if (!wallet) return;
    setMe((current) => (current ? { ...current, wallet } : current));
    setWalletBumped(true);
    window.clearTimeout(bumpTimer.current);
    bumpTimer.current = window.setTimeout(() => setWalletBumped(false), 600);
  }, []);

  const patchMe = useCallback((patch: Partial<Me>) => {
    setMe((current) => (current ? { ...current, ...patch } : current));
  }, []);

  const login = useCallback(
    async (username: string, password: string) => {
      await api.post('/auth/login', { username, password });
      await refresh();
    },
    [refresh],
  );

  const register = useCallback(
    async (username: string, password: string, trainerName?: string) => {
      await api.post('/auth/register', { username, password, trainerName });
      await refresh();
    },
    [refresh],
  );

  // A ref rather than state: this guards against the *second* call arriving
  // before the first has resolved, which state updates are too late for.
  // React's development double-invoke and an impatient double tap both land
  // here, and either one creating a second trainer would strand the first.
  const guestInFlight = useRef<Promise<void> | null>(null);

  const playAsGuest = useCallback(async () => {
    if (guestInFlight.current) return guestInFlight.current;
    const run = (async () => {
      try {
        await api.post('/auth/guest');
        await refresh();
      } finally {
        guestInFlight.current = null;
      }
    })();
    guestInFlight.current = run;
    return run;
  }, [refresh]);

  const claim = useCallback(
    async (input: { username: string; email: string; password: string }) => {
      await api.post('/auth/claim', input);
      await refresh();
    },
    [refresh],
  );

  const logout = useCallback(async () => {
    await api.post('/auth/logout');
    setMe(null);
  }, []);

  const celebrate = useCallback((unlocked: UnlockedAchievement[]) => {
    if (unlocked.length > 0) setPendingAchievements((current) => [...current, ...unlocked]);
  }, []);

  const clearAchievement = useCallback(() => {
    setPendingAchievements((current) => current.slice(1));
  }, []);

  const celebrateBadges = useCallback((awards: BadgeAward[]) => {
    if (awards.length > 0) setPendingBadges((current) => [...current, ...awards]);
  }, []);

  // The whole queue at once: BadgeFanfareQueue walks it internally so several
  // tiers crossed in one action build rather than each needing its own trip
  // through the store.
  const clearBadges = useCallback(() => setPendingBadges([]), []);

  const value = useMemo(
    () => ({
      me, loading, refresh, login, register, playAsGuest, claim, logout,
      setWallet, patchMe, walletBumped,
      celebrate, pendingAchievements, clearAchievement,
      celebrateBadges, pendingBadges, clearBadges,
    }),
    [
      me, loading, refresh, login, register, playAsGuest, claim, logout,
      setWallet, patchMe, walletBumped,
      celebrate, pendingAchievements, clearAchievement,
      celebrateBadges, pendingBadges, clearBadges,
    ],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession() {
  const context = useContext(SessionContext);
  if (!context) throw new Error('useSession must be used inside SessionProvider');
  return context;
}

/* -------------------------------------------------------------------------
   Data fetching
   ------------------------------------------------------------------------- */

export interface AsyncState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  reload: () => void;
  setData: (updater: T | ((current: T | null) => T | null)) => void;
}

/**
 * Small fetch-on-mount hook. Deliberately not a cache library: this game has
 * a handful of screens, each wants fresh server state on entry, and mutations
 * already return the updated rows to patch in locally.
 */
export function useAsync<T>(fetcher: () => Promise<T>, deps: unknown[] = []): AsyncState<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    fetcherRef
      .current()
      .then((result) => {
        if (!cancelled) setData(result);
      })
      .catch((caught: unknown) => {
        if (cancelled) return;
        setError(caught instanceof ApiError ? caught.message : 'Something went wrong.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce]);

  const reload = useCallback(() => setNonce((value) => value + 1), []);

  const update = useCallback((updater: T | ((current: T | null) => T | null)) => {
    setData((current) =>
      typeof updater === 'function' ? (updater as (c: T | null) => T | null)(current) : updater,
    );
  }, []);

  return { data, loading, error, reload, setData: update };
}
